import { WorkbookPage, SectionTitle, Paragraph } from '@/components/WorkbookPage';
import { MessageCircle, Heart, Shield, Users } from 'lucide-react';

export const FacilitatorNotePage = () => {
  return (
    <WorkbookPage pageId="facilitator-note" prevPage="program-description" nextPage="title" showNavigation={true}>
      <header className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <MessageCircle className="h-6 w-6 text-primary" />
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
            Facilitator Note
          </h1>
        </div>
        <p className="text-muted-foreground">
          Guidelines for leading group sessions
        </p>
      </header>
      
      <Paragraph>
        Thank you for facilitating this program. Your role is not to be a financial expert, 
        but to create a safe, supportive space where participants can learn and grow together. 
        The workbook does the teaching—you facilitate the conversation.
      </Paragraph>
      
      <SectionTitle>Group Agreements</SectionTitle>
      
      <Paragraph>
        Establish these agreements at your first meeting. Have participants verbally agree 
        or sign a group covenant:
      </Paragraph>
      
      <div className="space-y-4 my-6">
        <div className="flex items-start gap-4 bg-muted/30 rounded-lg p-4">
          <Shield className="h-5 w-5 text-primary shrink-0 mt-1" />
          <div>
            <h4 className="font-semibold">Confidentiality</h4>
            <p className="text-sm text-muted-foreground">
              What's shared in the group stays in the group. Personal financial details are 
              private. Stories can be shared generally ("someone in a group I was in...") 
              but never with identifying details.
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-4 bg-muted/30 rounded-lg p-4">
          <Heart className="h-5 w-5 text-primary shrink-0 mt-1" />
          <div>
            <h4 className="font-semibold">No Shame, No Judgment</h4>
            <p className="text-sm text-muted-foreground">
              We all have different backgrounds and experiences with money. There is no "right" 
              or "wrong" past—only the opportunity to make better choices going forward. 
              We support each other without judgment.
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-4 bg-muted/30 rounded-lg p-4">
          <Users className="h-5 w-5 text-primary shrink-0 mt-1" />
          <div>
            <h4 className="font-semibold">Respectful Discussion</h4>
            <p className="text-sm text-muted-foreground">
              We listen without interrupting. We share our own experiences rather than giving 
              unsolicited advice. We respect that everyone's situation is different.
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-4 bg-muted/30 rounded-lg p-4">
          <MessageCircle className="h-5 w-5 text-primary shrink-0 mt-1" />
          <div>
            <h4 className="font-semibold">Participation is Voluntary</h4>
            <p className="text-sm text-muted-foreground">
              No one is required to share personal details. Participants can always pass on 
              any question or discussion. Worksheets are personal and do not need to be shared.
            </p>
          </div>
        </div>
      </div>
      
      <SectionTitle>Facilitator Tips</SectionTitle>
      
      <div className="space-y-3 my-6">
        {[
          'Start each session with a brief check-in (how is everyone doing?)',
          'Review the previous rule briefly before introducing the new one',
          'Read key sections aloud—not everyone may read ahead',
          'Allow time for worksheet completion during group time',
          'Use open-ended questions: "What stood out to you?" "How does this apply to your situation?"',
          'Share your own experiences when appropriate—it builds trust',
          'Be prepared for emotional moments, especially around money stories',
          'End with encouragement and a specific action item for the week',
          'Remind participants this is education, not professional advice',
          'Celebrate progress—even small wins matter',
        ].map((tip, index) => (
          <div key={index} className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">
              {index + 1}
            </span>
            <span className="text-sm">{tip}</span>
          </div>
        ))}
      </div>
      
      <SectionTitle>Session Structure Suggestion</SectionTitle>
      
      <div className="bg-secondary/50 rounded-xl p-5 my-6">
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span>Check-in / Wins from the week</span>
            <span className="text-muted-foreground">10 min</span>
          </div>
          <div className="flex justify-between">
            <span>Review previous rule / Q&A</span>
            <span className="text-muted-foreground">10 min</span>
          </div>
          <div className="flex justify-between">
            <span>Read and discuss new rule</span>
            <span className="text-muted-foreground">20 min</span>
          </div>
          <div className="flex justify-between">
            <span>Worksheet time</span>
            <span className="text-muted-foreground">15 min</span>
          </div>
          <div className="flex justify-between">
            <span>Group discussion / Sharing</span>
            <span className="text-muted-foreground">15 min</span>
          </div>
          <div className="flex justify-between">
            <span>Closing / Action items</span>
            <span className="text-muted-foreground">5 min</span>
          </div>
          <div className="h-px bg-border my-2" />
          <div className="flex justify-between font-semibold">
            <span>Total</span>
            <span>~75 min</span>
          </div>
        </div>
      </div>
      
      <div className="mt-8 bg-primary/5 border border-primary/20 rounded-xl p-6 text-center">
        <p className="font-serif text-lg mb-2">
          Thank you for making a difference.
        </p>
        <p className="text-sm text-muted-foreground">
          The work you're doing matters. Financial stress affects every area of life. 
          By facilitating this program, you're helping people build not just better finances, 
          but better lives.
        </p>
      </div>
    </WorkbookPage>
  );
};
